import { ChartSvg } from "@/components/tool-kit/charts/chart-svg";
import { CHART_COLORS, CHART_INK } from "@/lib/tool-kit/palette";

const [BLUE, ORANGE] = CHART_COLORS;
const WIDTH = 560;
const HEIGHT = 260;
const LEFT = 52;
const RIGHT = 16;
const TOP = 16;
const BOTTOM = 48;

/** Trig results can differ in the last digit between Node and the browser; round them. */
const round = (value: number): number => Math.round(value * 10) / 10;

export interface Tick {
  value: number;
  label: string;
}

/**
 * A single curve over a numeric x axis, with gridlines at the ticks, an optional line at
 * y = 0 (the horizon) and an optional marker for "you are here".
 */
export function CurveChart({
  id,
  label,
  description,
  points,
  xDomain,
  yDomain,
  xTicks,
  yTicks,
  xTitle,
  yTitle,
  marker,
  zeroLine = false,
}: {
  id: string;
  label: string;
  description: string;
  points: readonly (readonly [number, number])[];
  xDomain: readonly [number, number];
  yDomain: readonly [number, number];
  xTicks: readonly Tick[];
  yTicks: readonly Tick[];
  xTitle: string;
  yTitle: string;
  marker?: { x: number; y: number } | null;
  zeroLine?: boolean;
}) {
  const plotWidth = WIDTH - LEFT - RIGHT;
  const plotHeight = HEIGHT - TOP - BOTTOM;
  const sx = (x: number): number =>
    round(LEFT + ((x - xDomain[0]) / (xDomain[1] - xDomain[0])) * plotWidth);
  const sy = (y: number): number =>
    round(
      TOP +
        plotHeight -
        ((y - yDomain[0]) / (yDomain[1] - yDomain[0])) * plotHeight
    );
  const path = points
    .map(([x, y], index) => `${index === 0 ? "M" : "L"}${sx(x)},${sy(y)}`)
    .join(" ");
  return (
    <ChartSvg
      description={description}
      height={HEIGHT}
      id={id}
      label={label}
      width={WIDTH}
    >
      {yTicks.map((tick) => (
        <g key={`y${tick.value}`}>
          <line
            stroke={CHART_INK.grid}
            x1={LEFT}
            x2={WIDTH - RIGHT}
            y1={sy(tick.value)}
            y2={sy(tick.value)}
          />
          <text
            fill={CHART_INK.muted}
            fontSize="11"
            textAnchor="end"
            x={LEFT - 6}
            y={sy(tick.value) + 4}
          >
            {tick.label}
          </text>
        </g>
      ))}
      {xTicks.map((tick) => (
        <g key={`x${tick.value}`}>
          <line
            stroke={CHART_INK.grid}
            x1={sx(tick.value)}
            x2={sx(tick.value)}
            y1={TOP}
            y2={TOP + plotHeight}
          />
          <text
            fill={CHART_INK.muted}
            fontSize="11"
            textAnchor="middle"
            x={sx(tick.value)}
            y={TOP + plotHeight + 16}
          >
            {tick.label}
          </text>
        </g>
      ))}
      {zeroLine ? (
        <line
          stroke={CHART_INK.axis}
          strokeWidth="1.5"
          x1={LEFT}
          x2={WIDTH - RIGHT}
          y1={sy(0)}
          y2={sy(0)}
        />
      ) : null}
      <rect
        fill="none"
        height={plotHeight}
        stroke={CHART_INK.axis}
        width={plotWidth}
        x={LEFT}
        y={TOP}
      />
      <path
        d={path}
        fill="none"
        stroke={BLUE}
        strokeLinejoin="round"
        strokeWidth="2.5"
      />
      {marker ? (
        <circle
          cx={sx(marker.x)}
          cy={sy(marker.y)}
          fill={ORANGE}
          r="5.5"
          stroke={CHART_INK.background}
          strokeWidth="1.5"
        />
      ) : null}
      <text
        fill={CHART_INK.text}
        fontSize="12"
        textAnchor="middle"
        x={LEFT + plotWidth / 2}
        y={HEIGHT - 8}
      >
        {xTitle}
      </text>
      <text
        fill={CHART_INK.text}
        fontSize="12"
        textAnchor="middle"
        transform={`rotate(-90 14 ${TOP + plotHeight / 2})`}
        x={14}
        y={TOP + plotHeight / 2}
      >
        {yTitle}
      </text>
    </ChartSvg>
  );
}
